import React, { useEffect, useMemo } from 'react';

import { FCC } from '@eluve/components';

import { TaskProcessorInterface } from './types';
import { TaskProcessorProviderContext } from './useTaskProcessor';

export interface TaskProcessorContextValue {
  taskProcessor: TaskProcessorInterface;
}

export const TaskProcessorProvider: FCC<{
  taskProcessor: TaskProcessorInterface;
}> = ({ children, taskProcessor }) => {
  useEffect(() => {
    const processTasksIfOnline = async () => {
      try {
        if (taskProcessor.isInitialized) {
          taskProcessor.resume();
        } else {
          await taskProcessor.initialize();
        }
        // TaskProcessor will automatically process queue after initialization
        // Note: TaskProcessor has internal isProcessing flag to prevent concurrent processing
      } catch {
        /* ignore */
      }
    };

    const pauseTaskQueue = () => {
      taskProcessor.pause();
    };

    if (navigator.onLine) {
      processTasksIfOnline();
    }

    window.addEventListener('online', processTasksIfOnline);
    window.addEventListener('offline', pauseTaskQueue);

    return () => {
      window.removeEventListener('online', processTasksIfOnline);
      window.removeEventListener('offline', pauseTaskQueue);
    };
  }, [taskProcessor]);

  const value = useMemo(() => ({ taskProcessor }), [taskProcessor]);

  return (
    <TaskProcessorProviderContext.Provider value={value}>
      {children}
    </TaskProcessorProviderContext.Provider>
  );
};
